import { RuntimeImage } from "../../runtime/RendererRuntime";
import { GuardedLink } from "../../runtime/DevPlaceholder";
import type { ImageFieldValue, LinkTarget } from "../../types";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, resolveImageValue } from "../../utils/imageOptions";

export type PersonCardItem = {
  title: string;
  description: string;
  href: LinkTarget;
};

type Props = PersonCardItem & {
  imageSrc?: ImageFieldValue;
  imageAlt?: string;
};

/** Card for person/role listings: image, bold title, subtitle in #0095DB, no arrow. */
export function PersonCard({
  title,
  description,
  href,
  imageSrc = "/media/d47f9f5af24fd7129274afad903571dd3819a330.png",
  imageAlt = "Photo",
}: Props) {
  const { src, options } = resolveImageValue(imageSrc);
  const resolvedSrc = src || "/media/d47f9f5af24fd7129274afad903571dd3819a330.png";
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  // Non-connected destination: the card stays visible (unwrap) but the link dies in
  // production; preview opens the dev modal. No more silent fallback to the home page.
  return (
    <GuardedLink href={href} label={title} block fallback="unwrap" className="group block h-full bg-white">
      <div className="grid h-full grid-cols-[2fr_3fr] md:flex md:flex-col">
        <div
          className={mergeClassNames(
            "relative min-h-[150px] overflow-hidden bg-slate-100 rounded-l-sm md:min-h-0 md:w-full md:rounded-none md:rounded-t-sm md:rounded-bl-none md:rounded-br-none md:rounded-tr-sm",
            overrideProps.className
          )}
          style={overrideProps.style}
        >
          <RuntimeImage
            src={resolvedSrc}
            alt={imageAlt}
            className="absolute inset-0 h-full w-full object-cover object-[center_30%] md:static md:h-44"
            style={objectPositionStyle}
          />
        </div>
        <div className="flex min-h-[150px] flex-col justify-center bg-[#eaf2fb] p-4 rounded-r-md md:min-h-[180px] md:rounded-none md:rounded-b-md">
          <div>
            <h3 className="text-[18px] xl:text-[20px] md:text-[20px] font-semibold leading-6" style={{ color: "#005CA9" }}>
              {title}
            </h3>
            <p className="mt-2 line-clamp-2 md:line-clamp-3 text-base xl:text-lg leading-5" style={{ color: "#0095DB" }}>
              {description}
            </p>
          </div>
        </div>
      </div>
    </GuardedLink>
  );
}
